import { Unit } from "@/types/enums/unit";
import {
  AddStock,
  DrawdownStock,
  ItemStock,
  Stock,
  StockFilters,
  StockGroupItem,
  StockStats,
  SupplierToStock,
  SupplierToStockCreate,
  SupplierToStockUpdate,
} from "@/types/inventory/stock";
import { Page } from "@/types/pagination";

import { requestData } from "@/api/api-config";
import { stockFiltersToSearchParams } from "@/api/provider/inventory/filter-search-params";

export async function listStocks(
  filters: StockFilters,
  options?: { signal?: AbortSignal; throwOnError?: boolean },
): Promise<Page<Stock> | null> {
  return await requestData<Page<Stock>>(
    "inventory/stocks/list",
    "get",
    {
      searchParams: stockFiltersToSearchParams(filters),
      signal: options?.signal,
    },
    options?.throwOnError !== true,
  );
}

export async function listStockGroups(
  filters: StockFilters,
): Promise<StockGroupItem[] | null> {
  return await requestData<StockGroupItem[]>("inventory/stocks/groups", "get", {
    searchParams: stockFiltersToSearchParams(filters),
  });
}

export async function getStock(id: string): Promise<Stock | null> {
  return await requestData<Stock>(`inventory/stocks/${id}`, "get");
}

export async function getStockStats(
  id: string,
  startDate?: string,
  endDate?: string,
): Promise<StockStats | null> {
  return await requestData<StockStats>(`inventory/stocks/${id}/stats`, "get", {
    searchParams: {
      ...(startDate && { start_date: startDate }),
      ...(endDate && { end_date: endDate }),
    },
  });
}

export async function searchStocks(
  searchTerm: string,
  itemIds: string[] | null,
): Promise<Stock[] | null> {
  return await requestData<Stock[]>("inventory/stocks/search", "get", {
    searchParams: { searchTerm, itemIds },
  });
}

export async function linkStockToItem(
  stockLink: ItemStock[],
): Promise<ItemStock[] | null> {
  return await requestData<ItemStock[]>(`inventory/stocks/link-stock`, "post", {
    json: stockLink,
  });
}

export async function unlinkStockFromItem(
  stockLink: ItemStock[],
): Promise<ItemStock[] | null> {
  return await requestData<ItemStock[]>(
    `inventory/stocks/unlink-stock`,
    "post",
    {
      json: stockLink,
    },
  );
}

export async function createStocks(
  stocks: Partial<Stock>[],
): Promise<Stock[] | null> {
  const stocksToCreate = stocks.map((stock) => ({
    ...stock,
    unit: stock.unit || Unit.UNITS,
  }));

  return await requestData<Stock[]>("inventory/stocks/create", "post", {
    json: stocksToCreate,
  });
}

export async function createStockFromImage(
  formData: FormData,
  options?: { signal?: AbortSignal; timeout?: number },
): Promise<Partial<Stock>[] | null> {
  // A single photo is analyzed well inside a minute; a multi-page PDF is rendered page by page
  // and sent as several images, so callers uploading one pass a longer timeout.
  const timeout = options?.timeout ?? 60000;

  return await requestData<Partial<Stock>[]>(
    "inventory/stocks/create-from-image",
    "post",
    {
      body: formData,
      headers: {
        Accept: "application/json",
      },
      signal: options?.signal,
    },
    false,
    timeout,
  );
}

export async function createStockFromCSV(
  formData: FormData,
  options?: { signal?: AbortSignal; timeout?: number },
): Promise<Partial<Stock>[] | null> {
  // Use configurable timeout with a reasonable default for AI processing
  const timeout = options?.timeout ?? 120000; // 2 minutes default for CSV processing

  return await requestData<Partial<Stock>[]>(
    "inventory/stocks/create-from-csv",
    "post",
    {
      body: formData,
      headers: {
        Accept: "application/json",
      },
      signal: options?.signal,
    },
    false,
    timeout,
  );
}

export async function updateStocks(
  stockUpdates: Partial<Stock>[],
): Promise<Stock[] | null> {
  return await requestData<Stock[]>(`inventory/stocks/update`, "put", {
    json: stockUpdates,
  });
}

export async function drawdownStock(
  drawdown: DrawdownStock,
): Promise<Stock | null> {
  return await requestData<Stock>(`inventory/stocks/drawdown`, "post", {
    json: drawdown,
  });
}

export async function addStock(addition: AddStock): Promise<Stock | null> {
  return await requestData<Stock>(
    `inventory/stocks/add`,
    "post",
    { json: addition },
    false,
  );
}

export async function archiveStock(id: string): Promise<Stock | null> {
  return await requestData<Stock>(`inventory/stocks/archive/${id}`, "delete");
}

export async function linkSupplierToStock(
  supplierLink: (SupplierToStockCreate | SupplierToStockUpdate)[],
): Promise<SupplierToStock[] | null> {
  return await requestData<SupplierToStock[]>(
    `inventory/stocks/link-supplier`,
    "post",
    {
      json: supplierLink,
    },
  );
}

export async function unlinkSupplierFromStock(
  supplierLink: SupplierToStock[],
): Promise<void | null> {
  await requestData<void>(`inventory/stocks/unlink-supplier`, "post", {
    json: supplierLink,
  });
}
